import { computed, isDevMode, signal } from '@angular/core';
import { HisColumnMetadata } from './his-column-metadata';
import { HisSearchResultRow, HisSearchResultSelectedRow, SelectableRow } from './his-search-result';

/**
 * Context holder managing the table rows and their selection.
 * 
 * Combines programatic change and implicit manipulation to the selection by the p-table.
 */
export class HisSearchResultRowContext {
    /** Exposed array containing the rows of the current search result page with actual data for p-tables `[value]`. */
    rows: HisSearchResultRow[] = [];
    /** Exposed array containing the selected rows of the current page, used for p-table internal referencing via `[(selection)]`.
     *  Must be an object array to conform with p-table internal operations. Stored objects are compared only using their `rowKey`. (This requires the setting `p-table.dataKey="rowKey"` ) */
    activePageSelection: HisSearchResultRow[] = [];
    /** Array containing all selected rows. Only holds an identifying reference to a row, but might contain the actual row data as well, depending on how it was selected. */
    private readonly allSelectedRows = signal<HisSearchResultSelectedRow[]>( [] );
    /** Public access to the current row selection. */
    public readonly selectedRows = this.allSelectedRows.asReadonly();
    /** Fast lookup set for selected row keys (string-normalized). */
    public readonly selectedRowKeySet = computed( () => new Set( this.allSelectedRows().map( row => String( row.rowKey ) ) ) );
    /** @devNote temporary converter access to the selections as a flat HisOption object for compatibility. This should be handled by consumers individually on use to reduce operations on selection changes. */
    public readonly selectedOptions = computed( () => this.allSelectedRows().map( row => row.toFlatHisOption() ) );

    /**
     * Setter for `rows` that applies the column metadata to each row.
     * @param rows rows to set
     * @param columnMeta the column metadata to enrich the rows
     * @param rowKeyColumn defines the column id of the column containing the rowKey
     * @devNote This enrichment was done in the search-result.component before.
     * If possible this should be moved to the search-service or the constructor of HisSearchResult.
     */
    public setRows( rows: HisSearchResultRow[], columnMeta: HisColumnMetadata[], rowKeyColumn: string ) {
        this.rows = rows;
        const replaceRowKey = !!rows && !!rowKeyColumn && rows[0]?.rowKey != rows[0]?.get( rowKeyColumn );
        this.rows.forEach( row => {
            row.metadata = columnMeta;
            if ( replaceRowKey ) { row.rowKey = row.get( rowKeyColumn ); }
        } );
    }

    /**
     * Checks whether the provided row is selected on the current page.
     * Should be preferred over `isRowSelected`, which checks the total selection, including rows outside of the page limits.
     * @param rowKey the identifier of the row
     * @returns true if the row is on the current page and selected
     */
    public isRowSelectedOnPage( rowKey: string ) {
        return !!this.activePageSelection.find( row => row.rowKey === String( rowKey ) );
    }

    /**
     * Checks whether the provided row is selected.
     * @param rowKey the identifier of the row
     * @returns true if the row is selected
     */
    public isRowSelected( rowKey: string ) {
        return this.selectedRowKeySet().has( String( rowKey ) );
    }

    /**
     * Selects the provided row, if it is not already selected.
     * Intended to be used with the `(onRowSelect)` event from the p-table. No manual management of `activePageSelection` required.
     * @param row the row to be selected
     */
    public selectRow( rowToSelect: HisSearchResultRow | HisSearchResultSelectedRow ) {

        if ( this.isRowSelected( rowToSelect.rowKey ) ) {
            if ( isDevMode() ) { console.debug( 'rowContext#selectRow - row to select was already selected', rowToSelect.rowKey ); }
            return;
        }

        this.addSelectedRows( this.toHisSearchResultSelectedRows( [rowToSelect] as HisSearchResultSelectedRow[] | HisSearchResultRow[] ) );
    }

    /**
     * For use with the p-table event `(onHeaderCheckboxToggle)`. The table handles the activePageSelection via `[(selection)]`, so this doesn't concern us here.
     */
    public selectAllOfPage() {
        this.addAllUnselectedRows( this.rows.map( row => row.toHisSearchResultSelectedRow() ) );
    }

    /**
     * Selects all of the provided rows in addition to the already selected rows.
     * Intended for programatic mass selection, therefore `activePageSelection` is handled manually as well to keep the p-tables visual synced with the model.
     * @param rowsToSelect the rows to be selected
     */
    public selectRows( rowsToSelect: HisSearchResultSelectedRow[] | HisSearchResultRow[] ) {
        this.addAllUnselectedRows( this.toHisSearchResultSelectedRows( rowsToSelect ) );
        this.updateActivePageSelection();
    }

    /**
     * Absolute setter for the row selection. Sets `selectedRows` to the content of the provided rows array.
     * This will lose any external reference to the supplied parameter to ensure immutability.
     * @param rows the rows to set as the selection
     */
    public setSelectedRows( rows: HisSearchResultSelectedRow[] | HisSearchResultRow[] ) {
        this.allSelectedRows.set( this.toHisSearchResultSelectedRows( rows ) );
        this.updateActivePageSelection();
    }

    /**
     * Deselects the provided row, if it is part of the selection.
     * Intended to be used with the `(onRowDeselect)` event from the p-table. No manual management of `activePageSelection` required.
     * @param rowToDeselect the row to be deselected, or its rowKey directly
     */
    public deselectRow( rowToDeselect: SelectableRow | string ) {
        const rowKeyToDeselect = typeof rowToDeselect === 'string' ? rowToDeselect : rowToDeselect.rowKey;

        if ( !this.isRowSelected( rowKeyToDeselect ) ) {
            if ( isDevMode() ) { console.debug( 'rowContext#deselectRow - row to deselect not selected', rowKeyToDeselect ); }
            return;
        }

        // filter implicitly creates a new array instance
        if ( isDevMode() ) { console.debug( 'rowContext#deselectRow - deselecting row', rowKeyToDeselect, 'from', [...this.selectedRowKeySet()] ); }
        this.allSelectedRows.update( current => current.filter( row => row.rowKey !== rowKeyToDeselect ) );
    }

    /**
     * For use with the p-table event `(onHeaderCheckboxToggle)`. The table handles the activePageSelection via `[(selection)]`, so this doesn't concern us here.
     */
    public deselectAllOfPage() {
        this.deselectRows( this.rows.map( row => row.rowKey ) );
    }

    /**
     * Delects all of the rows.
     * Intended for programatic mass deselection, therefore `activePageSelection` is handled manually as well to keep the p-tables visual synced with the model.
     * 
     * Subfunctions of his-search-result.component use a 'deselection' of a specified collection of rows. For this purpose the `rowsToDeselect` parameter can
     * be used. If an array is supplied, only the rows from that array will be deselected, otherwise ALL selected rows will be deselected.
     */
    public deselectRows( rowsToDeselect?: SelectableRow[] | string[] ) {
        if ( !rowsToDeselect ) {
            this.allSelectedRows.set( [] );
            this.activePageSelection = [];
            if ( isDevMode() ) { console.debug( 'rowContext#deselectRows - deselecting all rows' ); }
            return;
        }

        if ( rowsToDeselect.length === 0 ) {
            if ( isDevMode() ) { console.debug( 'rowContext#deselectRows - no rows were provided' ); }
            return; // early termination on empty array makes type assertions on a non empty array easier
        }

        if ( rowsToDeselect.length === 1 ) {
            this.deselectRow( rowsToDeselect[0] );
            this.updateActivePageSelection();
            return;
        }

        // transform the input to a string[] of rowKeys that should be deselected
        const rowKeys = typeof rowsToDeselect[0] === 'object'
            ? ( rowsToDeselect as SelectableRow[] ).map( row => row.rowKey )
            : ( rowsToDeselect as string[] ).map( String ); // ensure string type in case of number inputs
        if ( isDevMode() ) { console.debug( 'rowContext#deselectRows - deselecting rows', rowKeys, 'from', [...this.selectedRowKeySet()] ); }

        // filter the rowKeys for all that CAN be deselected
        const rowKeysToDeselect = new Set( this.filterDeselectableRowKeys( rowKeys ) );
        if ( rowKeysToDeselect.size > 0 ) {
            // create a new selection array and update the selection; 
            const newRowSelection = this.allSelectedRows().filter( row => !rowKeysToDeselect.has( row.rowKey ) );
            this.allSelectedRows.set( [...newRowSelection] );
            this.updateActivePageSelection();
            if ( isDevMode() ) { console.debug( 'rowContext#deselectRows - deselecting rows - new selection:', [...this.selectedRowKeySet()] ); }
        }
    }

    /**
     * Filters an array of rowKeys intended for deselection and removes any that are not currently selected.
     * @param rowKeys the rowKeys to filter
     * @returns a filtered array of rowKeys that CAN be deselected
     */
    private filterDeselectableRowKeys( rowKeys: string[] ) {
        const selectedRowKeySet = this.selectedRowKeySet();
        const isRowKeySelected = ( rowKey: string ) => selectedRowKeySet.has( rowKey );
        
        const result = rowKeys.filter( rowKey => isRowKeySelected( rowKey ) );
        if ( isDevMode() && result.length !== rowKeys.length ) {
            console.debug( 'rowContext#deselectAll - at least one row to deselect was not selected' );
        }
        return result;
    }

    /**
     * Does a manual calculation of the `activePageSelection` and updates it accordingly.
     * Use cases include `selectAll`, pagination and changes to `maxResultRows`.
     */
    public updateActivePageSelection() {
        const selection: HisSearchResultRow[] = [];
        for ( const row of this.rows ) {
            if ( this.isRowSelected( row.rowKey ) ) {
                selection.push( row );
            }
        }
        this.activePageSelection = selection;
    }

    /** Adds all provided rows to the selection, if they were not selected already. Ignores every selected row. */
    private addAllUnselectedRows( rowsToAdd: HisSearchResultSelectedRow[] ) {
        const rowsToSelect = rowsToAdd.filter( row => !this.isRowSelected( row.rowKey ) );
        this.addSelectedRows( rowsToSelect );
    }

    /** Convenience function to update the rowSelection by adding elements. Ensures the signal only updates for actual additions. */
    private addSelectedRows( rowsToAdd: HisSearchResultSelectedRow[] ) {
        if ( rowsToAdd.length > 0 ) {
            if ( isDevMode() ) { console.debug( 'rowContext#selectRows - selecting rows', rowsToAdd ); }
            this.allSelectedRows.update( current => [...current, ...rowsToAdd] );
        }
    }

    private isSelectedRowInstance( row: any ): row is HisSearchResultSelectedRow {
        return !!row && (row instanceof HisSearchResultSelectedRow || typeof row.toFlatHisOption === 'function');
    }

    /** Transforms the rows array if necessary. Returns a new array instance. */
    private toHisSearchResultSelectedRows( rows: HisSearchResultSelectedRow[] | HisSearchResultRow[] ): HisSearchResultSelectedRow[] {
        if ( this.isSelectedRowInstance( rows[0] ) ) {
            return [...rows] as HisSearchResultSelectedRow[];
        }
        return rows.map( row => ( row as HisSearchResultRow ).toHisSearchResultSelectedRow() );
    }
}
